import React, { useEffect, useState } from 'react';
import { usePermissions, Title, useTranslate } from 'react-admin';
import { Box, Typography, Paper, Alert } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';

// Services
import { getUsers } from '../services/usersService';

// Components
import DataGridTable from '../components/table/DataGridTable';
import PageMessage from '../components/misc/PageMessage';
import FilterRow from '../components/misc/FilterRow';

// Definitions
import { USERS_TABLE } from '../definitions';

// Utils
import { validateUserFilters } from '../utils/userUtils';

// Constants — varsayılan liste (arama yokken); aramada API tüm seti tarar
const LAST_X_ROWS = 100;

const UsersPage = () => {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  // Unified search: email, phone, or name
  const [search, setSearch] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const { permissions } = usePermissions();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const validation = validateUserFilters({ q: searchFilter });
      if (!validation.isValid) {
        setError(Object.values(validation.errors).join(', '));
        setData([]);
        setTotal(0);
        return;
      }

      const result = await getUsers({
        search: searchFilter,
        last: LAST_X_ROWS,
      });
      setData(result.data ?? []);
      setTotal(result.total ?? result.data?.length ?? 0);
    } catch (err) {
      setError(translate('users.load_error', { error: err.message, _: `Kullanıcılar yüklenemedi: ${err.message}` }));
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (permissions) {
      fetchData();
    }
  }, [searchFilter, permissions]);

  const CustomTableHeader = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );

  if (!permissions) {
    return <PageMessage message={translate('users.permissions_loading', { _: 'Kullanıcı Yetkileri Yükleniyor...' })} type="info" />;
  }

  return (
    <>
      <Title title={translate('users.title', { _: 'Kullanıcılar' })} />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          {translate('users.title', { _: 'Kullanıcılar' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {translate('users.desc', { _: 'E-posta, telefon numarası veya isme göre ara (tüm kullanıcılar taranır).' })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 720 }}>
          <FilterRow
            value={search}
            filterValue={searchFilter}
            setValue={setSearch}
            setFilterValue={setSearchFilter}
            label={translate('users.search_label', { _: 'Arama' })}
            placeholder={translate('users.search_placeholder', { _: 'E-posta, telefon veya isim...' })}
            type="text"
            useBrowserNative={true}
          />
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!error && !isLoading ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {searchFilter
              ? translate('users.results_count', { count: total, query: searchFilter, _: `${total} sonuç (“${searchFilter}”)` })
              : translate('users.latest_count', { count: data.length, _: `Son ${data.length} kullanıcı gösteriliyor (herkesi bulmak için aramayı kullanın)` })}
          </Typography>
        ) : null}
      </Paper>

      <Paper sx={{ flex: 1, p: 2, pt: 0 }}>
        <DataGridTable
          config={USERS_TABLE}
          rows={data}
          columns={USERS_TABLE.columns}
          loading={isLoading}
          slots={{ toolbar: CustomTableHeader }}
        />
      </Paper>
    </>
  );
};

export default UsersPage;
